import React from 'react';
import ReactDOMServer from 'react-dom/server';
import BeaconDoubleDropdownMetricColumnChart from 'src/components/BeaconV2Components/BeaconMetricColumnChart/BeaconDoubleDropdownMetricColumnChart';
import {
  PROMOTION_QUERY_PARAM,
  PromotionMetricOptions,
  promotionMetricToFieldName,
  promotionTypeToDisplayName
} from 'src/components/BeaconV2Components/ExperimentalLayout/Prices/Promotions/constants';
import { GenericXAxisLabel } from 'src/components/BeaconV2Components/TopEntityChartV2/GenericXAxisLabel';
import TopEntityChartV2Loading from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2Loading';
import AggregationBuilder from 'src/components/BeaconV2Components/utils/AggregationBuilder';
import { AdvancedSearchParsedDatum } from 'src/serverProxy/useAdvancedSearchData';
import { getQueryParamValue, safeDivide } from 'src/utils/app';
import { BEACON_INDEX_NAMES } from 'src/utils/entityDefinitions/indexes/beaconIndexNames';

const PromotionsDealTypeColumnChart = () => {
  const selectedMetric =
    promotionMetricToFieldName[getQueryParamValue(PROMOTION_QUERY_PARAM, PromotionMetricOptions.PROMOTION_COUNT)];

  return (
    <BeaconDoubleDropdownMetricColumnChart
      xAxisFormatter={(xAxisCategory) => {
        return ReactDOMServer.renderToStaticMarkup(<GenericXAxisLabel xAxisCategory={xAxisCategory} />);
      }}
      preProcessData={(data: AdvancedSearchParsedDatum[], field) => {
        // Depending on the metric selected, we need to calculate additional values for the chart.
        switch (field.name) {
          case 'shareOfRetailSales': {
            // Get total retail sales from the secondary aggregated data set (by retailerId) for the current time period
            const currentTotalRetailSales =
              data?.[0]?.rawResponse?.primary?.[0]?.aggregations?.by_retailerId?.[0]?.value;

            // Get total retail sales from the secondary aggregated data set (by retailerId) for the comparison time period
            const comparisonTotalRetailSales =
              data?.[0]?.rawResponse?.comparison?.[0]?.aggregations?.by_retailerId?.[0]?.value;
            return data.map((datum) => {
              return {
                ...datum,
                values: {
                  ...datum.values,
                  shareOfRetailSales_computed_value: safeDivide(
                    datum?.values?.retailSales_sum_value,
                    currentTotalRetailSales
                  )
                },
                comparisonValues: {
                  ...datum.comparisonValues,
                  shareOfRetailSales_computed_value: safeDivide(
                    datum?.comparisonValues?.retailSales_sum_value,
                    comparisonTotalRetailSales
                  )
                }
              };
            });
          }
          case 'shareOfUnitsSold': {
            // Get total units sold from the secondary aggregated data set (by retailerId) for the current time period
            const currentTotalUnitsSold = data?.[0]?.rawResponse?.primary?.[0]?.aggregations?.by_retailerId?.[0]?.value;

            // Get total units sold from the secondary aggregated data set (by retailerId) for the comparison time period
            const comparisonTotalUnitsSold =
              data?.[0]?.rawResponse?.comparison?.[0]?.aggregations?.by_retailerId?.[0]?.value;
            return data.map((datum) => {
              return {
                ...datum,
                values: {
                  ...datum.values,
                  shareOfUnitsSold_computed_value: safeDivide(datum?.values?.unitsSold_sum_value, currentTotalUnitsSold)
                },
                comparisonValues: {
                  ...datum.comparisonValues,
                  shareOfUnitsSold_computed_value: safeDivide(
                    datum?.comparisonValues?.unitsSold_sum_value,
                    comparisonTotalUnitsSold
                  )
                }
              };
            });
          }
          // We swap count for the value to determine an accurate count of promotions
          case 'stacklineSku':
            return data.map((datum) => ({
              ...datum,
              values: {
                ...datum.values,
                stacklineSku_cardinality_value: datum?.values?.stacklineSku_cardinality_count ?? 0
              },
              comparisonValues: {
                ...datum.comparisonValues,
                stacklineSku_cardinality_value: datum?.comparisonValues?.stacklineSku_cardinality_count ?? 0
              }
            }));
          default:
            return data;
        }
      }}
      postProcessData={(data) => {
        if (data) {
          return data.map((row) => {
            const [promotionType, value] = row;
            const displayName = promotionTypeToDisplayName[promotionType] || promotionType;
            return [displayName, Math.abs(value)];
          });
        } else {
          return data;
        }
      }}
      beaconChartWithLegendPropsBuilder={(props) => ({
        ...props,
        loadingComponent: <TopEntityChartV2Loading />
      })}
      selectedField={selectedMetric}
      fields={[
        { fieldName: 'stacklineSku', label: 'Promotion Count' },
        { fieldName: 'averagePromotionDiscount', label: 'Average Discount' },
        { fieldName: 'shareOfRetailSales', label: 'Share of Retail Sales' },
        { fieldName: 'shareOfUnitsSold', label: 'Share of Units Sold' }
      ]}
      useAdvancedSearchDataArgs={{
        requestId: 'promotionsByDealType',
        includeRawResponse: true, // We need the raw response to calculate the share of retail sales and units sold
        addFieldsFromPrimaryResponse: false,
        requestBuilder(builder, comparison, fields) {
          // Avoid duplicating the aggregation in the comparison request since it is cloned from the primary request
          // Add the additional aggregation for the retail sales and units sold
          if (fields?.[0]?.name === 'stacklineSku') {
            builder.addConditionRangeFilter('retailSales', 1);
          }

          if (!comparison) {
            if (fields?.[0]?.name === 'shareOfRetailSales') {
              let promoTypeAggBuilder: AggregationBuilder = null;
              builder.modifyAggregation((aggBuilder) => {
                aggBuilder.clearAggregationFields();
                aggBuilder.addAggregationField('Retail Sales', 'retailSales', 'sum', true);

                promoTypeAggBuilder = aggBuilder.clone();

                return aggBuilder;
              });
              builder.addAggregation(
                promoTypeAggBuilder
                  .clearAggregationFields()
                  .setGroupByFieldName('retailerId')
                  .addAggregationField('Retail Sales Denominator', 'retailSalesPrev', 'sum', true)
                  .build()
              );
            } else if (fields?.[0]?.name === 'shareOfUnitsSold') {
              let promoTypeAggBuilder: AggregationBuilder = null;
              builder.modifyAggregation((aggBuilder) => {
                aggBuilder.clearAggregationFields();
                aggBuilder.addAggregationField('Units Sold', 'unitsSold', 'sum', true);

                promoTypeAggBuilder = aggBuilder.clone();

                return aggBuilder;
              });
              builder.addAggregation(
                promoTypeAggBuilder
                  .clearAggregationFields()
                  .setGroupByFieldName('retailerId')
                  .addAggregationField('Units Sold Denominator', 'unitsSoldPrev', 'sum', true)
                  .build()
              );
            }
          }
          return builder;
        }
      }}
      showSingleGroupBy
      groupByFields={[
        {
          id: 'promoType',
          label: 'Deal Type',
          indexName: BEACON_INDEX_NAMES.PROMOTIONS
        }
      ]}
    />
  );
};

export default PromotionsDealTypeColumnChart;
